import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { liveMarketService, tenderService } from "../../services/api";
import { ROLES } from "../../config/roles";

// Shared club-side tender actions (Message Club / View Pitches / Apply Pitch),
// extracted verbatim from club/TenderCard.js so the card AND the list-view
// row use the SAME handlers/behaviour/permission flow — single source, no
// reimplementation. `loading` is shared so a button disables while its action
// runs, exactly like the card.
//
// The DRAFT transitions (post now, schedule, remove date) were added to this
// same hook rather than to a second one, for the same reason the file exists:
// the draft card, the draft list row and the preview modal all offer them, and
// they must not drift apart. `onChanged` is the parent's refetch — a draft that
// goes live has to leave the Drafts tab and the counters have to follow, and
// neither can be faked in the browser.
export const useClubTenderActions = (
  tender,
  { isStaffView = false, staffInClub = true, onChanged = null } = {}
) => {
  const navigate = useNavigate();
  const { currentRole } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);

  const viewPitches = () => {
    if (isStaffView) {
      if (currentRole === ROLES.Coach) {
        navigate(`/coach/tenders/${tender.id}/pitches`, { state: { tender } });
      } else {
        navigate(`/staff/tenders/${tender.id}/pitches`, { state: { tender } });
      }
    } else {
      navigate(`/club/tenders/${tender.id}/pitches`, { state: { tender } });
    }
  };

  const applyPitch = () => {
    if (currentRole === ROLES.Coach) {
      navigate(`/coach/tenders/${tender.id}/pitch`, { state: { tender } });
    } else {
      navigate(`/staff/tenders/${tender.id}/pitch`, { state: { tender } });
    }
  };

  const messageClub = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const clubId =
        tender?.club_id || tender?.club?.id || tender?.club?.club_id;
      if (!clubId) {
        toast.error("Club information not available for messaging");
        setLoading(false);
        return;
      }
      const response = await liveMarketService.createClubChat(clubId);
      if (response.success) {
        navigate("/inbox", {
          state: {
            channelId: response.channelId,
            channelType: response.channelType,
          },
        });
      } else {
        toast.error(response.error || "Failed to start chat with club");
      }
    } catch (error) {
      console.error("Error creating club chat:", error);
      toast.error("Failed to start chat with club");
    } finally {
      setLoading(false);
    }
  };

  // Mirror TenderCard.handlePrimaryAction: staff-not-in-club applies a pitch,
  // everyone else messages the club.
  const primaryAction = () => {
    if (isStaffView && !staffInClub) {
      applyPitch();
      return;
    }
    messageClub();
  };

  // ── Draft transitions ───────────────────────────────────────────────────
  // One shape for all three: run, report, let the parent refetch. Nothing is
  // patched into the row in the browser, because publishing is a server-side
  // status switch that also starts the row's clocks.
  const runDraftAction = async (call, successMessage, failureMessage) => {
    if (draftLoading) return false;
    setDraftLoading(true);
    try {
      const response = await call();
      if (response?.success) {
        toast.success(successMessage);
        onChanged?.();
        return true;
      }
      toast.error(response?.error || failureMessage);
      return false;
    } catch (error) {
      console.error(failureMessage, error);
      toast.error(failureMessage);
      return false;
    } finally {
      setDraftLoading(false);
    }
  };

  // draft -> active, right now. The same single status switch the autopost
  // sweep performs, so both paths land in the identical state.
  const publishDraftNow = () =>
    runDraftAction(
      () => tenderService.publishTenderNow(tender.id),
      "Request is live.",
      "Failed to publish request"
    );

  // Set or move the automatic publish moment. `isoString` is a full ISO date.
  const scheduleDraft = (isoString) =>
    runDraftAction(
      () => tenderService.scheduleTenderPublish(tender.id, isoString),
      "Publish date saved.",
      "Failed to schedule request"
    );

  // Take the date off. The row stays a draft, it just stops being due.
  const removeDraftSchedule = () =>
    runDraftAction(
      () => tenderService.clearTenderSchedule(tender.id),
      "Publish date removed.",
      "Failed to remove the date"
    );

  // A draft has no pitches and no history, so removing it is a real delete —
  // the same endpoint the card's existing delete uses, routed through here so
  // the list can refetch instead of reloading the whole page.
  const deleteDraft = () =>
    runDraftAction(
      () => tenderService.deleteTender(tender.id),
      "Draft deleted.",
      "Failed to delete draft"
    );

  // ── Archive transitions ─────────────────────────────────────────────────
  // Same runner, same reason as the draft block above: card, list row and
  // kebab fire the identical handler, and the parent refetches because both
  // transitions move the row between tabs and change the counters.

  // active -> archived. Archive instead of delete: the request moves to the
  // Archive tab, is kept twelve months and can be reactivated; nothing is
  // deleted and chats live on.
  const closeRequest = () =>
    runDraftAction(
      () => tenderService.closeTender(tender.id),
      "Request closed and moved to your archive.",
      "Failed to close request"
    );

  // archived -> active. The server restarts every clock and notifies agents,
  // exactly like publishing does.
  const reactivateRequest = () =>
    runDraftAction(
      () => tenderService.reactivateTender(tender.id),
      "Request is live again.",
      "Failed to reactivate request"
    );

  // Opens the existing create/edit wizard, prefilled. There is no second
  // wizard for drafts: pages/club/EditTender.js loads the row and fills the
  // same two steps.
  const editRequest = () => navigate(`/club/tenders/${tender.id}/edit`);

  return {
    loading,
    viewPitches,
    applyPitch,
    messageClub,
    primaryAction,
    draftLoading,
    publishDraftNow,
    scheduleDraft,
    removeDraftSchedule,
    deleteDraft,
    closeRequest,
    reactivateRequest,
    editRequest,
  };
};
